import { Link } from "react-router-dom";
import { NavBar } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { ArrowLeft } from "lucide-react";

export default function AccessibilityPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="accessibility-page">
      <SEO
        title="Accessibility Statement"
        description="GottaSecond accessibility commitment. We aim to conform to WCAG 2.1 AA standards to ensure digital accessibility for all users."
        path="/accessibility"
      />
      <NavBar>
        <Link to="/pricing" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Pricing</Link>
        <Link to="/login" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Login</Link>
        <Link to="/register" className="text-sm font-medium text-[#e8594a] hover:text-[#ff6b5a] transition-colors uppercase tracking-wider font-bold">Get Started</Link>
      </NavBar>

      <div className="max-w-3xl mx-auto px-6 py-24">
        <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-8 text-sm" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <h1 className="text-4xl font-bold mb-2 tracking-tight">Accessibility Statement</h1>
        <p className="text-white/40 mb-12 text-sm">Last updated: February 20, 2026</p>

        <div className="prose prose-invert prose-sm max-w-none space-y-8 text-white/70 leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Our Commitment</h2>
            <p>Studio Lifesaver LLC is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal access to all users.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Standards</h2>
            <p>GottaSecond aims to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at the AA level. These guidelines explain how to make web content more accessible to people with a wide range of disabilities, including visual, auditory, physical, speech, cognitive, language, learning, and neurological disabilities.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Measures Taken</h2>
            <p>We take the following measures to ensure accessibility of GottaSecond:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Semantic HTML elements are used throughout the application to convey meaning and structure.</li>
              <li>All interactive elements include appropriate ARIA labels and roles where necessary.</li>
              <li>Color contrast ratios meet or exceed WCAG AA standards for text and interactive elements.</li>
              <li>Keyboard navigation is supported for all core functionality.</li>
              <li>Form inputs include associated labels and error messages.</li>
              <li>Images include descriptive alt text where content is meaningful.</li>
              <li>The site is responsive and functions across a wide range of devices and screen sizes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Known Limitations</h2>
            <p>While we strive to ensure that all pages and content on GottaSecond are fully accessible, some content may not yet fully conform to WCAG 2.1 AA standards. We are actively working to identify and resolve accessibility issues. Known areas for improvement include:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Some complex interactive components (e.g., calendar pickers, map integrations) may have limited screen reader support due to third-party dependencies.</li>
              <li>Certain user-uploaded content (portfolio images) may lack descriptive alt text, as this content is provided by users.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Assistive Technology Compatibility</h2>
            <p>GottaSecond is designed to be compatible with the following assistive technologies:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Screen readers (NVDA, JAWS, VoiceOver)</li>
              <li>Screen magnification software</li>
              <li>Speech recognition software</li>
              <li>Keyboard-only navigation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Feedback</h2>
            <p>We welcome your feedback on the accessibility of GottaSecond. If you encounter any accessibility barriers or have suggestions for improvement, please contact us:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Email: <strong className="text-white">support@gottasecond.com</strong></li>
              <li>Subject line: "Accessibility Feedback"</li>
            </ul>
            <p>We aim to respond to accessibility feedback within 5 business days and to resolve reported issues as quickly as possible.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Enforcement</h2>
            <p>If you are not satisfied with our response to your accessibility concern, you may file a complaint with the U.S. Department of Justice, Civil Rights Division, or your local accessibility enforcement authority.</p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
